import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { BookOpen, X, ExternalLink } from 'lucide-react';
import { useMenuContext } from '../providers/MenuProvider';

// One guide per module, picked by the page's path. Modules without a guide show no button.
const MODULE_GUIDES: { prefix: string; url: string; title: string }[] = [
  { prefix: '/purchase', url: '/docs/Purchase-Management-User-Guide-Bilingual.html', title: 'Purchase Management — User Guide' },
  { prefix: '/parking', url: '/docs/Parking-Management-User-Guide-Bilingual.html', title: 'Parking Management — User Guide' },
];

/**
 * Floating, admin-only button shown on module pages that have a user guide.
 * Opens that module's guide in an in-app modal viewer.
 */
export function UserGuideButton() {
  const { isAdmin } = useMenuContext();
  const { pathname } = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const guide = MODULE_GUIDES.find(g => pathname === g.prefix || pathname.startsWith(g.prefix + '/'));

  // Only Super Admin / system-role users see the guide button, and only inside a module that has one.
  if (!isAdmin || !guide) {
    return null;
  }

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        title="User Guide"
        aria-label="Open user guide"
        className="fixed top-1.5 right-4 z-30 flex h-6 w-6 items-center justify-center rounded-full text-white shadow-md transition-all duration-300 hover:scale-110 hover:shadow-lg"
        style={{
          background: `linear-gradient(to bottom right, var(--color-primary-500), var(--color-primary-600))`,
        }}
      >
        <BookOpen className="h-3.5 w-3.5" />
      </button>

      {isOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          >
            <div
              className="flex h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal header */}
              <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
                <div className="flex items-center gap-2 font-semibold text-gray-800">
                  <BookOpen className="h-5 w-5 text-primary-600" />
                  <span>{guide.title}</span>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={guide.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open in new tab"
                    className="flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm text-gray-600 transition-colors hover:bg-gray-100"
                  >
                    <ExternalLink className="h-4 w-4" />
                    <span className="hidden sm:inline">New tab</span>
                  </a>
                  <button
                    onClick={() => setIsOpen(false)}
                    title="Close"
                    aria-label="Close"
                    className="rounded-lg p-1.5 text-gray-600 transition-colors hover:bg-gray-100"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Document viewer */}
              <iframe
                src={guide.url}
                title="User Guide"
                className="h-full w-full flex-1 border-0"
              />
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
